import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { apiPath } from '../config/apiBase';
import { ModuleShell } from './module-ui';
import { getDisplayTimeZone, setDisplayTimeZone } from '../modules/tickets/utils/datetime';

const API = apiPath('/api');

const TIMEZONES = [
  { value: 'Asia/Kolkata', label: 'India — Asia/Kolkata' },
  { value: 'Asia/Dubai', label: 'Gulf — Asia/Dubai' },
  { value: 'Asia/Singapore', label: 'Singapore — Asia/Singapore' },
  { value: 'Asia/Tokyo', label: 'Japan — Asia/Tokyo' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong — Asia/Hong_Kong' },
  { value: 'Europe/London', label: 'United Kingdom — Europe/London' },
  { value: 'Europe/Berlin', label: 'Central Europe — Europe/Berlin' },
  { value: 'America/New_York', label: 'US Eastern — America/New_York' },
  { value: 'America/Chicago', label: 'US Central — America/Chicago' },
  { value: 'America/Los_Angeles', label: 'US Pacific — America/Los_Angeles' },
  { value: 'Australia/Sydney', label: 'Australia Eastern — Australia/Sydney' },
  { value: 'UTC', label: 'UTC' },
];

function preview(timeZone) {
  const now = new Date();
  try {
    return new Intl.DateTimeFormat('en-IN', {
      timeZone,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).format(now);
  } catch {
    return 'That timezone name is not recognized';
  }
}

function AdminSettingsPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'super_admin' || user?.role === 'admin';
  const [timezone, setTimezone] = useState(getDisplayTimeZone());
  const [custom, setCustom] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/settings/display`, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      const name = res.data?.timezone || 'Asia/Kolkata';
      setTimezone(name);
      setDisplayTimeZone(name);
      if (!TIMEZONES.some((z) => z.value === name)) setCustom(name);
    }).catch(() => {});
  }, []);

  const selected = custom.trim() || timezone;
  const sample = useMemo(() => preview(selected), [selected]);
  const known = TIMEZONES.some((z) => z.value === selected);

  const save = async () => {
    setSaving(true);
    setError('');
    setSaved('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/settings/display`, { timezone: selected }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      const name = res.data?.timezone || selected;
      setTimezone(name);
      setDisplayTimeZone(name);
      if (TIMEZONES.some((z) => z.value === name)) setCustom('');
      setSaved(`Times now use ${name}${res.data?.abbreviation ? ` (${res.data.abbreviation})` : ''}.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save the timezone');
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <div style={{ padding: 12 }}>
        <h1 style={{ fontSize: 15, fontWeight: 700 }}>Access denied</h1>
        <p style={{ color: '#6B7280' }}>Only an admin can change the system timezone.</p>
      </div>
    );
  }

  return (
    <ModuleShell
      title="Settings"
      subtitle="Choose the timezone used for every time in the app, the client widget, emails, and reports. Tickets stay stored in UTC."
    >
      <div style={{ maxWidth: 560 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          Display timezone
        </label>
        <select
          value={known ? selected : ''}
          onChange={(e) => {
            setCustom('');
            setTimezone(e.target.value);
            setSaved('');
          }}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #D1D5DB' }}
        >
          {!known && <option value="">Custom timezone</option>}
          {TIMEZONES.map((z) => (
            <option key={z.value} value={z.value}>{z.label}</option>
          ))}
        </select>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '14px 0 6px' }}>
          Other IANA timezone
        </label>
        <input
          value={custom}
          onChange={(e) => { setCustom(e.target.value); setSaved(''); }}
          placeholder="e.g. Asia/Qatar"
          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #D1D5DB', boxSizing: 'border-box' }}
        />
        <p style={{ fontSize: 13, color: '#374151', marginTop: 14 }}>
          Current time in this zone: <strong>{sample}</strong>
        </p>
        <p style={{ fontSize: 12, color: '#6B7280' }}>
          Stored times stay in UTC and are converted when they are shown. Changing this does not edit old tickets.
        </p>
        {error && <p style={{ color: '#B91C1C', fontSize: 13 }}>{error}</p>}
        {saved && <p style={{ color: '#065F46', fontSize: 13 }}>{saved}</p>}
        <button type="button" className="btn-primary" onClick={save} disabled={saving || !selected} style={{ marginTop: 8 }}>
          {saving ? 'Saving…' : 'Save timezone'}
        </button>
      </div>
    </ModuleShell>
  );
}

export default AdminSettingsPage;
